import { kebabCase } from '@fergdigitalcommerce/fergy-utilities';
import { type FunctionComponent, useRef, useState } from 'react';
import { type RequiredNonNullable } from '../../../../../@types/build';
import { type ProductOptionFieldsFragment, type ProductResourceFieldsFragment } from '../../../__generated__/graphql-client-types';
import { handleCarouselMobileSwipe, onLeftButtonClick, onRightButtonClick } from '../../../helpers/carousel/carousel.helper';
import { chunkEvery } from '../../../helpers/general-helper/general-helper';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { getProductResourcesByOptions } from '../../../helpers/product-helper/configuration/product-option-resource.helper';
import { useClientOnlyBreakpoints } from '../../../hooks/breakpoints/client-only-breakpoints.hook';
import { ClickableElement } from '../../buttons';
import { CarouselButtonsWithCount } from '../../carousel-components/carousel-buttons-with-count/carousel-buttons-with-count.component';
import { type CloudinaryImageProps } from '../../images/cloudinary-image/cloudinary-image.component';
import { ProductOptionActionCard } from './product-option-action-card.component';
import { defaultCardCarousel, transitionStyle, cardsStyle } from './product-option-cards.css';

export type ProductOptionCardsProps = {
	name: string;
	options: ProductOptionFieldsFragment[];
};

export type ProductOptionCardCarouselSlideProps = {
	onClick: () => void;
	resource: RequiredNonNullable<ProductResourceFieldsFragment>;
};

const ProductOptionCardCarouselSlide: FunctionComponent<ProductOptionCardCarouselSlideProps> = ({ onClick, resource }) => {
	const { variantId, finishes } = resource;
	const currentFinish = finishes.find(({ id }) => id === variantId) || finishes[0];
	const image: CloudinaryImageProps = {
		...currentFinish.image,
		publicID: currentFinish.image.id,
		bypassLazyLoad: true,
		options: {}
	};
	return (
		<ClickableElement
			ariaLabel="Click product option card"
			onClick={onClick}
			onKeyDown={handleKeys(['Enter', ' '], onClick)}
			tabIndex={0}
			data-testid="slide-button">
			<ProductOptionActionCard {...resource} image={image} />
		</ClickableElement>
	);
};

export type ProductOptionCardCarouselProps = {
	onSelect: (option: ProductOptionFieldsFragment & { resource: RequiredNonNullable<ProductResourceFieldsFragment> }) => void;
} & ProductOptionCardsProps;

/**
 * Product Option Card Carousel Component
 */
export const ProductOptionCardCarousel: FunctionComponent<ProductOptionCardCarouselProps> = ({ name, options, onSelect }) => {
	const [activeIndex, setActiveIndex] = useState<number>(0);
	const [translateValuePercentageCard, setTranslateValuePercentageCard] = useState<number>(0);
	const [prevTouchMoveValue, setPrevTouchMoveValue] = useState<number>(0);
	const { isLarge } = useClientOnlyBreakpoints();
	// Mobile touch event refs
	const touchStartXRef = useRef<number>(0);
	const touchMoveXRef = useRef<number>(0);
	const sliderContainerRef = useRef<HTMLDivElement>(null);

	const resources = getProductResourcesByOptions(options);
	const areResourcesValid = resources.length > 0 && resources.length === options.length; // one resource per option.
	const onCardClick = (selectedIndex: number) =>
		onSelect({
			...options[selectedIndex],
			resource: resources[selectedIndex]
		});

	const SLIDES_PER_VIEW = isLarge ? 3 : 2;
	const updatedResourcesListForCardsPerView = chunkEvery(resources, SLIDES_PER_VIEW);

	return areResourcesValid ? (
		<section
			className={`flex flex-column bg-theme-grey-lighter pa3 f5 items-start justify-between w-100 ${defaultCardCarousel}`}
			data-testid={kebabCase(name)}>
			<div className="flex flex-row items-center justify-between w-100 overflow-x-hidden">
				<div className="relative overflow-hidden w-100" data-testid="product-option-cards-carousel">
					<div
						className={`flex h-100 ${transitionStyle}`}
						data-testid="product-option-cards-slider"
						ref={sliderContainerRef}
						onTouchStart={(e) => (touchStartXRef.current = e.targetTouches[0].clientX)}
						onTouchMove={(e) => (touchMoveXRef.current = e.targetTouches[0].clientX)}
						onTouchEnd={() =>
							handleCarouselMobileSwipe(
								setTranslateValuePercentageCard,
								setActiveIndex,
								setPrevTouchMoveValue,
								prevTouchMoveValue,
								touchStartXRef.current,
								touchMoveXRef.current,
								activeIndex,
								updatedResourcesListForCardsPerView.length - 1
							)
						}
						style={{
							transform: `translate(${translateValuePercentageCard}%)`,
							scrollBehavior: 'smooth',
							touchAction: 'pan-y'
						}}>
						{updatedResourcesListForCardsPerView.map((productOptionCards, pageIndex) => (
							<div className={`flex flex-shrink-0 ga3 justify-center ${cardsStyle}`} key={pageIndex}>
								{productOptionCards.map((resource, slideIndex) => (
									<div className="w-inherit h-inherit" key={resource.variantId}>
										<ProductOptionCardCarouselSlide
											resource={resource}
											onClick={() => onCardClick(pageIndex * SLIDES_PER_VIEW + slideIndex)}
										/>
									</div>
								))}
							</div>
						))}
					</div>
				</div>
			</div>
			{options.length > 3 ? (
				<CarouselButtonsWithCount
					onLeftButtonClick={() => onLeftButtonClick(setTranslateValuePercentageCard, setActiveIndex, activeIndex)}
					onRightButtonClick={() => onRightButtonClick(setTranslateValuePercentageCard, setActiveIndex, activeIndex)}
					activeIndex={activeIndex}
					totalItems={updatedResourcesListForCardsPerView.length}
					className="theme-black justify-center mt3"
				/>
			) : null}
		</section>
	) : null;
};
